/**
 * Engraving Option – Custom Element
 * Handles checkbox toggle, text input, character counting,
 * and syncing the engraving text to a hidden line‑item property
 * field inside the product form.
 */
if (!customElements.get('engraving-option')) {
  class EngravingOption extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-engraving-checkbox]');
      this.inputWrap = this.querySelector('[data-engraving-input-wrap]');
      this.textInput = this.querySelector('[data-engraving-text]');
      this.counter = this.querySelector('[data-engraving-counter]');
      this.maxChars = parseInt(this.dataset.maxChars || '20', 10);

      // Find the hidden property input inside the product form
      const formId = this.dataset.formId;
      this.hiddenInput = formId
        ? document.querySelector(`#${formId} [data-engraving-property]`)
        : this.closest('product-info')?.querySelector('[data-engraving-property]');

      // Price update elements
      this.productInfo = this.closest('product-info');
      this.priceEls = this.productInfo ? this.productInfo.querySelectorAll('[data-product-price]') : [];
      this.extraPrice = parseFloat(this.dataset.extraPrice || '0') * 100; // in cents
      
      // Store original HTML of each price element
      this.originalPrices = [];
      this.priceEls.forEach(el => {
        this.originalPrices.push(el.innerHTML);
      });
      
      this.engravingVariantId = this.dataset.engravingVariantId;

      this.form = formId 
        ? document.getElementById(formId) 
        : this.closest('product-info')?.querySelector('form');

      // Intercept fetch globally to inject engraving product and sync cart modifications
      if (!window.__engravingFetchIntercepted) {
        window.__engravingFetchIntercepted = true;
        const originalFetch = window.fetch;
        
        window.fetch = async function() {
          let url = arguments[0];
          let options = arguments[1];
          let isCartModify = typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'));

          // 1) ADD TO CART INTERCEPTION
          if (typeof url === 'string' && url.includes('/cart/add') && options && options.body) {
            const engravingOption = document.querySelector('engraving-option');
            if (engravingOption && engravingOption.checkbox && engravingOption.checkbox.checked && engravingOption.engravingVariantId) {
              const engravingId = parseInt(engravingOption.engravingVariantId, 10);
              const textValue = engravingOption.textInput ? engravingOption.textInput.value : '';
              const linkId = 'LNK_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

              if (options.body instanceof FormData) {
                const formData = options.body;
                const mainId = formData.get('id');
                const mainQty = formData.get('quantity') || 1;
                
                if (mainId) {
                  const newBody = {
                    items: [
                      {
                        id: parseInt(mainId, 10),
                        quantity: parseInt(mainQty, 10),
                        properties: {}
                      },
                      {
                        id: engravingId,
                        quantity: parseInt(mainQty, 10), // Ensure quantities match
                        properties: {
                          'Grabado': textValue,
                          '_link_id': linkId,
                          '_is_engraving': 'true'
                        }
                      }
                    ]
                  };

                  for (let [key, value] of formData.entries()) {
                     if (key.startsWith('properties[')) {
                        const propName = key.match(/\[(.*?)\]/)[1];
                        if (propName !== 'Grabado Láser') {
                          newBody.items[0].properties[propName] = value;
                        }
                     }
                  }
                  
                  newBody.items[0].properties['Texto Grabado'] = textValue;
                  newBody.items[0].properties['_link_id'] = linkId;
                  
                  options.method = 'POST';
                  options.headers = options.headers || {};
                  options.headers['Content-Type'] = 'application/json';
                  options.body = JSON.stringify(newBody);
                }
              } else if (typeof options.body === 'string') {
                try {
                  let jsonBody = JSON.parse(options.body);
                  if (jsonBody.id) {
                     jsonBody.items = [{
                       id: jsonBody.id,
                       quantity: jsonBody.quantity || 1,
                       properties: jsonBody.properties || {}
                     }];
                     delete jsonBody.id;
                     delete jsonBody.quantity;
                     delete jsonBody.properties;
                  }
                  
                  if (jsonBody.items) {
                    const primaryBlock = jsonBody.items[0];
                    const mainQty = primaryBlock.quantity || 1;
                    
                    jsonBody.items.push({
                      id: engravingId,
                      quantity: mainQty,
                      properties: { 'Grabado': textValue, '_link_id': linkId, '_is_engraving': 'true' }
                    });
                    
                    primaryBlock.properties = primaryBlock.properties || {};
                    primaryBlock.properties['Texto Grabado'] = textValue;
                    primaryBlock.properties['_link_id'] = linkId;
                  }
                  options.body = JSON.stringify(jsonBody);
                } catch(e) { console.error('Error parsing cart add JSON', e); }
              }
            }
          }
          
          // Let the normal (or modified) fetch proceed
          let response = await originalFetch.apply(this, arguments);
          
          // 2) CART SYNC NORMALIZATION AFTER MUTATION
          if (isCartModify) {
            try {
              // Read the current cart state from the response 
              // (most cart endpoints return the full cart object)
              const clonedResponse = response.clone();
              const cartData = await clonedResponse.json();
              
              if (cartData && cartData.items) {
                // Determine if we need to fix any discrepancies 
                let updatesNeeded = {}; // { line_number: quantity_or_properties }
                let hasChanges = false;
                
                // Group items by _link_id
                const linkedGroups = {};
                cartData.items.forEach((item, index) => {
                  const props = item.properties;
                  if (props && props['_link_id']) {
                    const linkId = props['_link_id'];
                    if (!linkedGroups[linkId]) linkedGroups[linkId] = { main: null, side: null };
                    
                    if (props['_is_engraving'] === 'true') {
                       linkedGroups[linkId].side = { item, line: index + 1 }; 
                    } else {
                       linkedGroups[linkId].main = { item, line: index + 1 };
                    }
                  }
                });
                
                // Analyze groups for orphans or quantity mismatches
                Object.values(linkedGroups).forEach(group => {
                  // If main product was deleted, side (engraving) must be deleted
                  if (!group.main && group.side) {
                    updatesNeeded[group.side.line] = 0; // Remove side
                    hasChanges = true;
                  }
                  // If side was deleted, main product must lose its engraving properties
                  else if (group.main && !group.side) {
                    // Update main line item properties to remove the link and text
                    const cleanProps = { ...group.main.item.properties };
                    // To delete a property in Shopify /cart/change, we must pass it as an empty string
                    cleanProps['_link_id'] = '';
                    cleanProps['Texto Grabado'] = '';
                    
                    updatesNeeded[group.main.line] = {
                      quantity: group.main.item.quantity,
                      properties: cleanProps
                    };
                    hasChanges = true;
                  }
                  // If both exist, enforce that Side quantity aligns with Main quantity
                  else if (group.main && group.side) {
                    if (group.main.item.quantity !== group.side.item.quantity) {
                      updatesNeeded[group.side.line] = {
                        quantity: group.main.item.quantity,
                        properties: group.side.item.properties // Preserve properties
                      };
                      hasChanges = true;
                    }
                  }
                });
                
                // If we need to fix the cart, we do consecutive synchronous updates 
                // and then return the final normalized cart response to the theme
                if (hasChanges) {
                  let finalCart = cartData;
                  const root = window.Shopify?.routes?.root || '/';
                  
                  // Iteratively apply changes
                  for (let [line, updateVal] of Object.entries(updatesNeeded)) {
                    let payload = { line: parseInt(line, 10) };
                    if (typeof updateVal === 'number') { payload.quantity = updateVal; }
                    else { payload.quantity = updateVal.quantity; payload.properties = updateVal.properties; }
                    
                    const updateRes = await originalFetch(root + (root.endsWith('/') ? '' : '/') + 'cart/change.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload)
                    });
                    finalCart = await updateRes.json();
                  }
                  
                  // Return a mocked Response object containing the normalized cart 
                  // so the theme's downstream JS renders the correct UI
                  const blob = new Blob([JSON.stringify(finalCart)], { type: 'application/json' });
                  return new Response(blob, {
                    status: 200,
                    statusText: 'OK',
                    headers: new Headers({ 'Content-Type': 'application/json' })
                  });
                }
              }
            } catch(e) {
              console.error('Error normalizing cart links', e);
            }
          }
          
          return response;
        };
      }

      if (this.checkbox) {
        this.checkbox.addEventListener('change', this._onToggle.bind(this));
      }

      if (this.textInput) {
        this.textInput.addEventListener('input', this._onInput.bind(this));
      }

      // Set initial state
      this._onToggle();
    }

    _onToggle() {
      const checked = this.checkbox?.checked || false;

      if (this.inputWrap) {
        this.inputWrap.style.display = checked ? 'block' : 'none';
      }

      // Clear the property when unchecked
      if (!checked) {
        if (this.textInput) this.textInput.value = '';
        this._syncHidden('');
        this._updateCounter(0);
      } else {
        // Sync whatever is there in case it was toggled back on
        if (this.textInput) this._syncHidden(this.textInput.value);
      }

      this._updateVisualPrice(checked);
    }

    _updateVisualPrice(checked) {
      // Intentionally left blank:
      // User requested not to add the 5€ visually to the product page main ticker.
      // It will only be added physically to the cart via the hidden product injection.
    }

    _onInput() {
      let value = this.textInput.value;

      // Enforce max characters
      if (value.length > this.maxChars) {
        value = value.substring(0, this.maxChars);
        this.textInput.value = value;
      }

      this._updateCounter(value.length);
      this._syncHidden(value);
    }

    _updateCounter(len) {
      if (this.counter) {
        this.counter.textContent = `${len} / ${this.maxChars}`;
      }
    }

    _syncHidden(value) {
      if (this.hiddenInput) {
        this.hiddenInput.value = value;
        // Remove the property entirely if empty so it doesn't appear in the cart
        if (!value) {
          this.hiddenInput.name = '';
        } else {
          this.hiddenInput.name = 'properties[Grabado Láser]';
        }
      }
    }
  }

  customElements.define('engraving-option', EngravingOption);
}
